import inflection from "inflection";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";

import { LoadingGroup, LoadingSkeleton } from "capitalrx-components-2.0/lib/general";
import { Shelf, Spacing, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Semibold, Subtitle } from "capitalrx-components-2.0/lib/typography";

import { DrugDetailsResponse } from "@/api/types";
import { StackOrShelf } from "@/common/components/StackOrShelf";
import { ThemedButton as BackButton } from "@/common/components/themed";
import { ThemedButton } from "@/premium/themedComponents";

interface SearchDetailsSectionProps {
  readonly drugDetails?: DrugDetailsResponse & {
    readonly drug_type: string,
  };
  readonly onBackButtonClicked: () => void;
  readonly onPrintButtonClicked?: () => void;
  readonly isLoading?: boolean;
}


const inflectQuantity = (quantity: number, form: string): string => {
  return inflection.inflect(`${Number(Number(quantity).toFixed(3))} ${form}`, quantity);
};

export const SearchDetailsSection = ({ drugDetails, onBackButtonClicked, onPrintButtonClicked, isLoading = false }: SearchDetailsSectionProps) => {
  const { t: translate } = useTranslation();
  const [searchParams, _] = useSearchParams();
  const pharmacyName = searchParams.get("pharmacyName");
  const zipCode = searchParams.get("zipCode");
  const showPharmacyName = pharmacyName && pharmacyName !== "";
  const { useSpacing } = useResponsive({
    useSpacing: {
      desktop: "large" as Spacing,
      tablet: "large" as Spacing,
      mobile: "none" as Spacing,
    },
  });
  return (
    <Stack spacing="none">
      <LoadingGroup loading={isLoading}>
        <LoadingGroup.Placeholder
          fallback={
            <>
              <Stack spacing="mini">
                <LoadingSkeleton style={{ height: "18px", width: "100px" }} />
                <LoadingSkeleton style={{ height: "36px", width: "150px" }} />
              </Stack>
              <LoadingSkeleton style={{ height: "20px", width: "200px" }} />
            </>
          }
        >
          <Stack spacing="none">
            <Shelf>
              <BackButton className="back-button" appearance="text" icon="arrow-left" onClick={onBackButtonClicked}>
                {translate("BUTTONS.backButton.back")}
              </BackButton>
              <Shelf.Spacer />
            </Shelf>
            <Stack spacing="mini">
              <StackOrShelf>
                <Subtitle>{translate("FIND_PHARMACIES.drugDetailsForm.results")}</Subtitle>
                <Shelf.Spacer />
                {!drugDetails && !!onPrintButtonClicked && (
                  <Stack>
                    <ThemedButton onClick={onPrintButtonClicked} appearance="text" icon="print">
                      {translate("PHARMACIES.pharmacies.printList")}
                    </ThemedButton>
                  </Stack>
                )}
              </StackOrShelf>
              <Body>
                <Semibold>
                  {translate("FIND_PHARMACIES.findPharmacies.zipCode")}
                : </Semibold>
                {zipCode}
              </Body>
            </Stack>
            {showPharmacyName && (
              <Body>
                <Semibold>
                  {translate("FIND_PHARMACIES.findPharmacies.pharmacyName")}
                : </Semibold>
                {pharmacyName}
              </Body>
            )}
          </Stack>
          {!!drugDetails && (
            <StackOrShelf spacing={useSpacing}>
              <Body>
                <Semibold>
                  {translate("FIND_PHARMACIES.findPharmacies.drugName")}
                : </Semibold>
                {drugDetails?.drug_name}
              </Body>
              <Body>
                <Semibold>
                  {translate("FIND_PHARMACIES.drugDetailsForm.dosage")}
                : </Semibold>
                {drugDetails?.dosage}
              </Body>
              <Body>
                <Semibold>
                  {translate("FIND_PHARMACIES.drugDetailsForm.quantity")}
                : </Semibold>
                {inflectQuantity(drugDetails?.quantity, drugDetails?.dosage_form)}
              </Body>
              <Body>
                <Semibold>
                  {translate("FIND_PHARMACIES.drugDetailsForm.daysSupply")}
                : </Semibold>
                {drugDetails?.days_supply} {translate("FIND_PHARMACIES.drugDetailsForm.days")}
              </Body>
              <Shelf.Spacer />
            </StackOrShelf>
          )}
        </LoadingGroup.Placeholder>
      </LoadingGroup>
    </Stack>
  );
};
